import * as React from 'react';
import { useState, useCallback } from 'react';
import { DevItemComment } from './models';
import LocalDiv from '../../../localization/LocalDiv';
import { css } from '@emotion/core';
import LocalBtn from '../../../localization/LocalBtn';
import { Str } from '../../../localization/translationKeys';
import LocalTextArea from '../../../localization/LocalTextArea';
import { COLORS } from '../../../globalStyle/colors';
import { useDevCommenterLabel } from './hooks';
import { TEXT_AREA } from '../../../forms/styles/textarea';
import Spacer from '../../../layout/Spacer';
import { FLEX_ROW, FLEX_COLUMN } from '../../../layout/flexBox';
import { btnStyle } from '../../../forms/styles/button';
import FaceIcon from '../../../icons/FaceIcon';


const styles = {
  root:  [
    FLEX_ROW,
    css`
      align-items: flex-start;
    `,
  ],
  form:  {
    root:       [
      FLEX_COLUMN,
      css`
        align-items: flex-start;
        flex-basis: 100%;
      `,
    ],
    textArea:   {
      default:  [
        TEXT_AREA,
        css`
          width: 100%;
          font-size: 12px;
          ::placeholder {
            font-style: italic;
          }
        `,
      ],
      disabled: [
        css`
          background-color: transparent;
          padding: 0;
        `,
      ],
    },
    actionBtns: {
      root:      [
        FLEX_ROW,
      ],
      saveBtn:   [
        btnStyle.primary(COLORS.BLUE),
      ],
      cancelBtn: [
        btnStyle.transparent(COLORS.BLUE),
        css`
        border-color: transparent;
      `,
      ],
    },
  },
  green: [
    css`color: ${COLORS.GREEN};`,
  ],
};


type Props = {
  comment: DevItemComment;
  inEditMode: boolean;
  handleClick: () => void;
  handleCancel: () => void;
  updateComment: (comment: string) => void;
  className?: string;
};


/**
 * DevItemCommentView: a component that renders a comment from a participant or collaborator, with facilities to edit or
 * delete that comment (available only to the comment author)
 */
const DevItemCommentView: React.FC<Props> = (
  {
    comment,
    inEditMode,
    handleClick,
    handleCancel,
    updateComment,
    className,
  },
) => {
  const [commentText, setCommentText] = useState<string>();

  const handleClickTextArea = useCallback(
    () => {
      !commentText && setCommentText(comment.comment);
      handleClick();
    },
    [comment.comment, commentText, handleClick],
  );

  const handleClickCancelBtn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      handleCancel();
    },
    [handleCancel],
  );

  const handleClickSaveBtn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      updateComment(commentText ?? '');
      handleCancel();
      setCommentText(undefined);
    },
    [commentText, handleCancel, updateComment],
  );

  const handleChangeCommentText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCommentText(event.target.value);
    },
    [],
  );

  const { commenterLabel, isFromCurrentUser } = useDevCommenterLabel(comment);

  return (
    <LocalDiv className={className} css={styles.root}>
      <FaceIcon sizePx={30} fill={isFromCurrentUser ? undefined : COLORS.GREEN} />
      <Spacer sizePx={12} />
      <form css={styles.form.root}>
        <LocalDiv css={!isFromCurrentUser && styles.green}>
          {commenterLabel} - {new Date(comment.lastUpdated).toLocaleString()}
        </LocalDiv>
        <Spacer sizePx={12} />
        <LocalTextArea
          value={inEditMode ? commentText : comment.comment}
          placeholderTranslation=''
          onClick={handleClickTextArea}
          onChange={handleChangeCommentText}
          readOnly={!isFromCurrentUser}
          css={[styles.form.textArea.default, !inEditMode && styles.form.textArea.disabled]}
          spellCheck={inEditMode} // This is awkward to see when edit mode is turned off.
        />
        <Spacer sizePx={12} />
        {
          inEditMode && (
            <LocalDiv css={styles.form.actionBtns.root}>
              <LocalBtn
                type='submit'
                str={Str.Save}
                onClick={handleClickSaveBtn}
                disabled={!commentText || commentText === comment.comment}
                css={styles.form.actionBtns.saveBtn}
              />
              <Spacer sizePx={12} />
              <LocalBtn str={Str.Cancel} onClick={handleClickCancelBtn} css={styles.form.actionBtns.cancelBtn} />
            </LocalDiv>
          )
        }
      </form>
    </LocalDiv>
  );
};

export default DevItemCommentView;
